import { useCallback, useEffect, useState } from "react";
import { generateActivationKey, readActivationKeyStatus } from "./activationKeyApi.js";

import { DetailSkeleton } from "../components/Skeleton.jsx";
// P150: the pane a sysadmin uses to issue the key a worker types into the
// Workmate X phone app, alongside their passport number.
//
// The whole point of this screen is that the key is shown ONCE. The server
// stores a PBKDF2 hash and nothing else, so there is no endpoint that could
// show it again and this component never tries: once the reveal is dismissed
// the plaintext is dropped from React state too. Every string below is
// written to make that unmistakable, because a sysadmin who assumes they can
// come back for it later will end up regenerating and silently signing the
// worker's phone out.

const COPY_RESET_MS = 2000;

function describeStatusError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูสถานะรหัสเปิดใช้งานของแรงงานคนนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Worker Profile นี้";
  }
  return "ไม่สามารถโหลดสถานะรหัสเปิดใช้งานได้ กรุณาลองใหม่อีกครั้ง";
}

function describeGenerateError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return error?.body?.message ?? "เฉพาะผู้ดูแลระบบ (SuperAdmin) เท่านั้นที่สร้างรหัสเปิดใช้งานได้";
  }
  if (error?.status === 409) {
    return error?.body?.message ?? "แรงงานคนนี้ยังไม่มีเลขพาสปอร์ตในระบบ จึงยังสร้างรหัสไม่ได้";
  }
  return "สร้างรหัสเปิดใช้งานไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

export default function ActivationKeyPane({ workerId }) {
  const [statusState, setStatusState] = useState({ status: "loading", data: null, error: null });
  const [generateState, setGenerateState] = useState({ status: "idle", error: null });
  const [revealed, setRevealed] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadStatus = useCallback(() => {
    if (!workerId) {
      return undefined;
    }
    let cancelled = false;
    setStatusState({ status: "loading", data: null, error: null });
    readActivationKeyStatus(workerId)
      .then((data) => {
        if (!cancelled) {
          setStatusState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setStatusState({ status: "error", data: null, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workerId]);

  useEffect(() => loadStatus(), [loadStatus]);

  // Switching to a different worker must never leave the previous worker's
  // key on screen.
  useEffect(() => {
    setRevealed(null);
    setConfirming(false);
    setGenerateState({ status: "idle", error: null });
  }, [workerId]);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = setTimeout(() => setCopied(false), COPY_RESET_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  async function handleGenerate() {
    setConfirming(false);
    setGenerateState({ status: "saving", error: null });
    try {
      const response = await generateActivationKey(workerId);
      setRevealed(response);
      setGenerateState({ status: "idle", error: null });
      if (response?.status) {
        // The generate response already carries the refreshed status, so the
        // pane updates without a second round trip.
        setStatusState({ status: "ready", data: response.status, error: null });
      } else {
        loadStatus();
      }
    } catch (error) {
      setGenerateState({ status: "error", error });
    }
  }

  async function handleCopy() {
    if (!revealed?.activationKey) {
      return;
    }
    try {
      await navigator.clipboard.writeText(revealed.activationKey);
      setCopied(true);
    } catch {
      // Clipboard access can be denied by the browser. The key is on screen
      // in selectable text either way, so this is not worth an error state.
    }
  }

  if (statusState.status === "loading") {
    return (
      <section className="wx-wp-dsec">
        <DetailSkeleton label="กำลังโหลดสถานะรหัสเปิดใช้งาน..." />
      </section>
    );
  }

  if (statusState.status === "error") {
    return (
      <section className="wx-wp-dsec">
        <p className="wx-state-body" role="alert">
          {describeStatusError(statusState.error)}
        </p>
      </section>
    );
  }

  const status = statusState.data ?? {};
  const generatedAt = formatDateTimeTh(status.generatedAtUtc);
  const saving = generateState.status === "saving";

  return (
    <section className="wx-wp-dsec wx-wp-akey">
      <h3>รหัสเปิดใช้งานแอปมือถือ</h3>
      <p className="wx-wp-akey-lede">
        แรงงานจะกรอก <strong>เลขพาสปอร์ต</strong> คู่กับ <strong>รหัสเปิดใช้งาน</strong> นี้ในแอปมือถือ
        เพื่อผูกเครื่องเข้ากับโปรไฟล์ของตัวเอง รหัสนี้สร้างได้โดยผู้ดูแลระบบเท่านั้น
      </p>

      <dl className="wx-wp-akey-facts">
        <div>
          <dt>สถานะรหัส</dt>
          <dd>
            {status.hasSecret ? (
              <span className="wx-badge wx-badge--success">ออกรหัสแล้ว (ชุดที่ {status.secretVersion})</span>
            ) : (
              <span className="wx-badge wx-badge--danger">ยังไม่เคยออกรหัส</span>
            )}
          </dd>
        </div>
        <div>
          <dt>เลขพาสปอร์ต</dt>
          <dd>
            {status.hasPassportNumber ? (
              <code className="wx-wp-akey-mask">{status.passportNumberMasked}</code>
            ) : (
              <span className="wx-wp-akey-warn">ยังไม่มีในโปรไฟล์ — ต้องกรอกก่อนจึงจะออกรหัสได้</span>
            )}
          </dd>
        </div>
        <div>
          <dt>ออกรหัสล่าสุด</dt>
          <dd>{generatedAt ?? "-"}</dd>
        </div>
        <div>
          <dt>เครื่องที่ผูกอยู่</dt>
          <dd>{status.boundDeviceCount ?? 0} เครื่อง</dd>
        </div>
      </dl>

      {revealed && (
        <div className="wx-wp-akey-reveal" role="status">
          <h4>รหัสเปิดใช้งานใหม่ (แสดงครั้งเดียวเท่านั้น)</h4>
          <p className="wx-wp-akey-reveal-warn">
            ระบบเก็บเฉพาะค่าที่เข้ารหัสไว้ ไม่ได้เก็บตัวรหัสจริง — ปิดหน้านี้แล้วจะเปิดดูซ้ำไม่ได้
            หากทำหาย ต้องสร้างใหม่ ซึ่งจะทำให้เครื่องที่ผูกไว้เดิมหลุดทันที
          </p>
          <div className="wx-wp-akey-code-row">
            <code className="wx-wp-akey-code">{revealed.activationKey}</code>
            <button type="button" className="wx-button wx-button--ghost" onClick={handleCopy}>
              {copied ? "คัดลอกแล้ว" : "คัดลอก"}
            </button>
          </div>
          {revealed.revokedDeviceCount > 0 && (
            <p className="wx-wp-akey-revoked">
              เครื่องที่ผูกไว้เดิม {revealed.revokedDeviceCount} เครื่องถูกตัดสิทธิ์แล้ว
              และจะถูกเด้งกลับไปหน้าเปิดใช้งานเมื่อเปิดแอปครั้งถัดไป
            </p>
          )}
          <button type="button" className="wx-button wx-button--ghost" onClick={() => setRevealed(null)}>
            บันทึกรหัสแล้ว ปิดหน้านี้
          </button>
        </div>
      )}

      {generateState.status === "error" && (
        <p className="wx-wp-akey-error" role="alert">
          {describeGenerateError(generateState.error)}
        </p>
      )}

      {status.canGenerate ? (
        confirming ? (
          <div className="wx-wp-akey-confirm" role="alertdialog" aria-label="ยืนยันการสร้างรหัสเปิดใช้งาน">
            <p>
              {status.boundDeviceCount > 0
                ? `การสร้างรหัสใหม่จะตัดสิทธิ์เครื่องที่ผูกอยู่ ${status.boundDeviceCount} เครื่องทันที แรงงานต้องเปิดใช้งานใหม่ด้วยรหัสชุดใหม่ ยืนยันหรือไม่`
                : "ยืนยันสร้างรหัสเปิดใช้งานใหม่หรือไม่ รหัสจะแสดงเพียงครั้งเดียว"}
            </p>
            <div className="wx-wp-akey-actions">
              <button type="button" className="wx-button wx-button--primary" onClick={handleGenerate} disabled={saving}>
                {saving ? "กำลังสร้าง..." : "ยืนยันสร้างรหัส"}
              </button>
              <button type="button" className="wx-button wx-button--ghost" onClick={() => setConfirming(false)} disabled={saving}>
                ยกเลิก
              </button>
            </div>
          </div>
        ) : (
          <div className="wx-wp-akey-actions">
            <button
              type="button"
              className="wx-button wx-button--primary"
              onClick={() => setConfirming(true)}
              disabled={saving || !status.hasPassportNumber}
            >
              {status.hasSecret ? "สร้างรหัสใหม่" : "สร้างรหัสเปิดใช้งาน"}
            </button>
            {!status.hasPassportNumber && (
              // Not a cosmetic disable: without a passport number on the
              // profile the worker has nothing to type in alongside the key,
              // so the backend refuses to mint one at all.
              <span className="wx-wp-akey-hint">กรอกเลขพาสปอร์ตในแท็บ “ข้อมูลแรงงาน” ก่อน</span>
            )}
          </div>
        )
      ) : (
        <p className="wx-wp-akey-hint">เฉพาะผู้ดูแลระบบ (SuperAdmin) เท่านั้นที่สร้างรหัสเปิดใช้งานได้</p>
      )}
    </section>
  );
}
